import React, { useState, useRef, useEffect } from 'react';
import { Box, Paper, TextField } from '@mui/material';
import Draggable from 'react-draggable';
import { getStickyNotesRequest } from '~/api/patientReports/getStickyNotesRequest';
import { postStickyNotes } from '~/api/patientReports/postStickyNotes';
const StickyNotes = (props) => {
    const [content, setContent] = useState('');
    const [size, setSize] = useState({ width: 100, height: 80 }); // Default fallback
    const [isResizing, setIsResizing] = useState(false);
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragKey, setDragKey] = useState(0);
    const textFieldRef = useRef(null);
    const resizeRef = useRef(null);
    const containerRef = useRef(null);
    // Load sticky note content
    useEffect(() => {
        const loadContent = async () => {
            try {
                const result = await getStickyNotesRequest({ patientId: props.patientId });
                setContent(result.stickyNotes.noteContent || '');
            }
            catch (error) {
                console.error('Failed to load sticky note:', error);
            }
        };
        loadContent();
    }, [props.patientId]);
    // Set initial size based on parent container (50% width & height)
    useEffect(() => {
        if (containerRef.current) {
            const { offsetWidth, offsetHeight } = containerRef.current;
            setSize({
                width: offsetWidth,
                height: offsetHeight
            });
        }
    }, []);
    const handleResizeMouseDown = (e) => {
        e.stopPropagation();
        setIsResizing(true);
        setResizeStart({
            x: e.clientX,
            y: e.clientY,
            width: size.width,
            height: size.height
        });
    };
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isResizing) {
                const deltaX = e.clientX - resizeStart.x;
                const deltaY = e.clientY - resizeStart.y;
                setSize({
                    width: Math.max(100, resizeStart.width + deltaX),
                    height: Math.max(80, resizeStart.height + deltaY)
                });
            }
        };
        const handleMouseUp = () => setIsResizing(false);
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isResizing, resizeStart]);
    const handleDragStop = (e, data) => {
        setPosition({ x: data.x, y: data.y });
    };
    const resetPosition = () => {
        // setPosition({ x: 0, y: 0 });
        // setSize({ width: 200, height: 150 }); // Reset to default or re-calc with parent if needed
        // setResizeStart({ x: 0, y: 0, width: 0, height: 0 });
        setDragKey(prev => prev + 1);
    };
    const handleSave = async () => {
        try {
            await postStickyNotes({ patientId: props.patientId, content });
            resetPosition();
        }
        catch (error) {
            console.error('Failed to save sticky note:', error);
        }
    };
    return (React.createElement("div", { ref: containerRef, style: { width: '100%', height: '100%', position: 'relative' } },
        React.createElement(Draggable, { key: dragKey, handle: ".drag-handle", position: position, onStop: handleDragStop },
            React.createElement(Paper, { elevation: 6, onBlur: handleSave, sx: {
                    width: size.width,
                    height: size.height,
                    backgroundColor: '#FFF9C4',
                    overflow: 'hidden',
                    userSelect: 'none',
                    cursor: 'move',
                    position: 'absolute',
                    zIndex: 1000
                } },
                React.createElement(Box, { className: "drag-handle", sx: {
                        backgroundColor: '#FFF59D',
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' }
                    } }),
                React.createElement(Box, { sx: {
                        padding: '3px',
                        position: 'relative',
                        height: size.height - 28
                    } },
                    React.createElement(TextField, { inputRef: textFieldRef, multiline: true, fullWidth: true, variant: "standard", placeholder: "Click to add a note...", value: content, onChange: (e) => setContent(e.target.value), InputProps: {
                            disableUnderline: true,
                            sx: {
                                padding: '6px',
                                height: '100%',
                                alignItems: 'flex-start',
                                '& .MuiInputBase-input': {
                                    height: '100% !important',
                                    overflow: 'auto !important',
                                    resize: 'none',
                                    cursor: 'text',
                                    backgroundColor: 'transparent',
                                    fontSize: '0.875rem',
                                    fontFamily: 'Barlow',
                                    color: '#333',
                                    '&::-webkit-scrollbar': { width: '4px' },
                                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                        borderRadius: '2px',
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' }
                                    }
                                }
                            }
                        }, sx: {
                            height: '100%',
                            flex: '1',
                            '& .MuiInput-root': { height: '100%' }
                        } }),
                    React.createElement(Box, { ref: resizeRef, onMouseDown: handleResizeMouseDown, sx: {
                            marginLeft: 'auto',
                            marginTop: 'auto',
                            width: 8,
                            height: 8,
                            cursor: 'se-resize',
                            opacity: 0.5,
                            '&:hover': { opacity: 1 },
                            background: `linear-gradient(
                  -45deg,
                  transparent 30%,
                  #666 30%,
                  #666 40%,
                  transparent 40%,
                  transparent 60%,
                  #666 60%,
                  #666 70%,
                  transparent 70%
                )`
                        } }))))));
};
export default StickyNotes;
